/* ══════════════════════════════════════════════════════════════
   English (en) — Base Translation File
   ~500 keys covering all India Market Monitor pages
   ══════════════════════════════════════════════════════════════ */

export const en = {
    // ── App & Navigation ────────────────────────────────────
    app: {
        name: 'India Market Monitor',
        tagline: 'AI-Powered Indian Market Intelligence',
    },
    nav: {
        dashboard: 'Dashboard',
        strategy: 'Strategy',
        intelligence: 'Intelligence',
        markets: 'Markets',
        indiaMap: 'India Map',
        ipoEarnings: 'IPO & Earnings',
        backtest: 'Backtest',
        settings: 'Settings',
        search: 'Search companies, news, strategies…',
    },

    // ── Dashboard ───────────────────────────────────────────
    dashboard: {
        title: 'Dashboard',
        marketStatus: 'Market Status',
        open: 'OPEN',
        closed: 'CLOSED',
        preOpen: 'PRE-OPEN',
        holiday: 'HOLIDAY',
        strategyCard: 'Today\'s Strategy',
        marketRadar: 'Market Radar',
        fiiDii: 'FII / DII Flows',
        quickStats: 'Quick Stats',
        newsTicker: 'Priority News',
        liveTV: 'Live TV',
        viewDetails: 'View Details →',
        updatedAt: 'Updated at {{time}}',
    },

    // ── Strategy ────────────────────────────────────────────
    strategy: {
        title: 'Strategy',
        regime: 'Market Regime',
        regimeTypes: {
            trendingBull: 'Trending Bull',
            trendingBear: 'Trending Bear',
            sideways: 'Sideways / Range',
            highVol: 'High Volatility',
        },
        confidence: 'Confidence',
        entry: 'Entry',
        target: 'Target',
        stopLoss: 'Stop Loss',
        riskReward: 'Risk : Reward',
        signal: 'Signal',
        buy: 'BUY',
        sell: 'SELL',
        hold: 'HOLD',
        featureImportance: 'Feature Importance',
        topDrivers: 'Top 10 drivers today',
        totalFeatures: '{{count}} total features computed',
        performance: 'Model Performance',
        accuracy: 'Accuracy',
        healthy: 'Healthy',
        warning: 'Warning',
        degraded: 'Degraded',
        keyLevels: 'Key Levels',
        support: 'Support',
        resistance: 'Resistance',
        pivotPoint: 'Pivot Point',
        currentPrice: 'Current Price',
        strategyDetail: 'Strategy Detail',
        commentary: 'AI Commentary',
        sectors: 'Relevant Sectors',
    },

    // ── Intelligence ────────────────────────────────────────
    intelligence: {
        title: 'Intelligence',
        morningBrief: 'Morning Brief',
        priorityFeed: 'Priority Feed',
        sectorIntel: 'Sector Intelligence',
        earningsTracker: 'Earnings',
        eventTimeline: 'Events',
        newsSearch: 'Search',
        briefTitle: 'AI Morning Brief',
        briefDate: '{{date}} • Pre-Market Analysis',
        keyPoints: 'Key Points',
        overnightGlobal: 'Overnight Global',
        todayWatch: 'Today\'s Watch',
        sectorOutlook: 'Sector Outlook',
        source: 'Source',
        impact: 'Impact',
        critical: 'CRITICAL',
        high: 'HIGH',
        medium: 'MEDIUM',
        low: 'LOW',
        whyItMatters: 'Why It Matters',
        sentiment: 'Sentiment',
        bullish: 'Bullish',
        bearish: 'Bearish',
        neutral: 'Neutral',
    },

    // ── Markets ─────────────────────────────────────────────
    markets: {
        title: 'Markets',
        indices: 'Indices',
        options: 'Options',
        sectors: 'Sectors',
        commodities: 'Commodities',
        currency: 'Currency',
        breadth: 'Breadth',
        nifty50: 'NIFTY 50',
        sensex: 'SENSEX',
        bankNifty: 'BANK NIFTY',
        niftyIT: 'NIFTY IT',
        niftyMidcap: 'NIFTY MIDCAP',
        indiaVIX: 'INDIA VIX',
        week52Range: '52w: {{low}} – {{high}}',
        optionsChain: 'Options Chain',
        strike: 'Strike',
        callOI: 'Call OI',
        putOI: 'Put OI',
        pcr: 'PCR',
        maxPain: 'Max Pain',
        ivRank: 'IV Rank',
        spotPrice: 'Spot Price',
        sectorHeatmap: 'Sector Heatmap',
        change: 'Change',
        volume: 'Volume',
        advancing: 'Advancing',
        declining: 'Declining',
        unchanged: 'Unchanged',
        adRatio: 'A/D Ratio',
        newHighs: 'New 52w Highs',
        newLows: 'New 52w Lows',
        deliveryPct: 'Delivery %',
    },

    // ── IPO & Earnings ──────────────────────────────────────
    ipo: {
        title: 'IPO & Earnings',
        dashboard: 'IPO Dashboard',
        earningsTracker: 'Earnings Tracker',
        upcoming: 'Upcoming IPOs',
        live: 'Live IPO Subscription',
        listed: 'Recently Listed',
        issueSize: 'Issue Size',
        priceBand: 'Price Band',
        lotSize: 'Lot Size',
        opens: 'Opens',
        closes: 'Closes',
        gmp: 'GMP',
        subscription: 'Subscription',
        retail: 'Retail',
        qib: 'QIB',
        nii: 'NII',
        employee: 'Employee',
        totalSub: 'Total Subscription',
        listingGain: 'Listing Gain',
        currentReturn: 'Current Return',
        recommend: 'Recommendation',
        subscribe: 'Subscribe',
        avoid: 'Avoid',
        wait: 'Wait',
        earningsCalendar: 'Earnings Calendar',
        earningsResults: 'Q3 FY26 Results',
        revenue: 'Revenue',
        pat: 'PAT',
        revenueGrowth: 'Rev Growth',
        patGrowth: 'PAT Growth',
        verdict: 'Verdict',
        beat: 'BEAT',
        miss: 'MISS',
        inline: 'INLINE',
    },

    // ── Backtest ────────────────────────────────────────────
    backtest: {
        title: 'Backtest',
        equityCurve: 'Equity Curve',
        riskMetrics: 'Risk Metrics',
        monthlyReturns: 'Monthly Returns',
        regimeBreakdown: 'Regime Breakdown',
        tradeLog: 'Trade Log',
        totalReturn: 'Total Return',
        cagr: 'CAGR',
        sharpe: 'Sharpe',
        sortino: 'Sortino',
        maxDrawdown: 'Max Drawdown',
        winRate: 'Win Rate',
        profitFactor: 'Profit Factor',
        calmar: 'Calmar',
        expectancy: 'Expectancy',
        avgHold: 'Avg Hold',
        bestTrade: 'Best Trade',
        worstTrade: 'Worst Trade',
        trades: 'trades',
        strategy: 'Strategy',
        buyAndHold: 'Buy & Hold',
        positiveMonths: 'positive months',
        avgMonthly: 'Avg',
        date: 'Date',
        type: 'Type',
        entryPrice: 'Entry',
        exitPrice: 'Exit',
        pnl: 'P&L',
        holdDays: 'Hold',
    },

    // ── India Map ───────────────────────────────────────────
    map: {
        title: 'India Map',
        selectState: 'Select a state',
        gdp: 'GDP',
        growth: 'Growth',
        keySectors: 'Key Sectors',
        population: 'Population',
        perCapitaIncome: 'Per Capita Income',
        industrialOutput: 'Industrial Output',
        exportShare: 'Export Share',
    },

    // ── Settings ────────────────────────────────────────────
    settings: {
        title: 'Settings',
        appearance: 'Appearance',
        theme: 'Theme',
        darkMode: 'Dark Mode',
        lightMode: 'Light Mode',
        language: 'Language',
        selectLanguage: 'Select Language',
        currentLanguage: 'Current: {{lang}}',
        dataPreferences: 'Data Preferences',
        refreshInterval: 'Refresh Interval',
        cacheSettings: 'Cache Settings',
        apiKeys: 'API Keys',
        about: 'About',
        version: 'Version {{version}}',
    },

    // ── FII/DII ─────────────────────────────────────────────
    fiiDii: {
        fii: 'FII',
        dii: 'DII',
        net: 'Net',
        today: 'Today',
        fiveDay: '5-Day',
        thirtyDay: '30-Day',
        buyingStreak: 'FII buying {{count}} consecutive days',
        sellingStreak: 'FII selling {{count}} consecutive days',
        mixed: 'Mixed FII activity',
        source: 'Source: NSDL/CDSL',
        updatedAt: 'Updated at {{time}}',
    },

    // ── Market Radar ────────────────────────────────────────
    radar: {
        title: 'Market Radar',
        score: '{{score}}/10',
        buy: 'BUY',
        hold: 'HOLD',
        sell: 'SELL',
        signals: 'Signals',
    },

    // ── Sector Rotation ─────────────────────────────────────
    sectorRotation: {
        title: 'Sector Rotation',
        leading: 'Leading',
        weakening: 'Weakening',
        lagging: 'Lagging',
        improving: 'Improving',
        relativeStrength: 'Relative Strength',
        momentum: 'Momentum',
        oneMonth: '1M',
        threeMonth: '3M',
        sixMonth: '6M',
        inflows: 'Inflows',
        outflows: 'Outflows',
    },

    // ── Election Impact ─────────────────────────────────────
    election: {
        title: 'Election Impact',
        upcomingElections: 'Upcoming Elections',
        state: 'State',
        expectedDate: 'Expected Date',
        incumbent: 'Incumbent',
        keyContestants: 'Key Contestants',
        historicalImpact: 'Historical Nifty Impact',
        priorDays: '30 Days Before',
        electionWeek: 'Election Week',
        afterDays: '30 Days After',
        sectorImpact: 'Sector Impact (if party wins)',
        avgReturn: 'Avg Nifty Return',
    },

    // ── Budget Impact ───────────────────────────────────────
    budget: {
        title: 'Budget Impact',
        unionBudget: 'Union Budget 2026-27',
        countdown: 'Countdown',
        daysLeft: '{{days}} days left',
        presented: 'Presented on {{date}}',
        historicalReaction: 'Historical Budget Day Nifty',
        sectorAllocation: 'Sector Allocation Changes (YoY)',
        increase: 'Increase',
        decrease: 'Decrease',
        keyAnnouncements: 'Key Policy Announcements',
        sectorWiseImpact: 'Sector-Wise Impact',
        positiveFor: 'Positive For',
        negativeFor: 'Negative For',
    },

    // ── Common ──────────────────────────────────────────────
    common: {
        loading: 'Loading…',
        error: 'Something went wrong',
        retry: 'Retry',
        noData: 'No data available',
        viewAll: 'View All',
        close: 'Close',
        save: 'Save',
        cancel: 'Cancel',
        yes: 'Yes',
        no: 'No',
        crore: 'Cr',
        lakh: 'L',
        rupee: '₹',
        percent: '%',
        positive: 'Positive',
        negative: 'Negative',
        lastUpdated: 'Last updated: {{time}}',
        preMarket: 'Pre-Market',
        postMarket: 'Post-Market',
        marketHours: 'Market hours: 9:15 AM – 3:30 PM IST',
        disclaimer: 'Not financial advice. For informational purposes only.',
    },
};
